import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Search,
  RefreshCw,
  Clock,
  ExternalLink,
  Phone,
  Repeat2,
} from 'lucide-react';
import { FaultDetectorService } from '../../services/faultDetectorService';

const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const pad2 = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const todayStr = () => toYmd(new Date());

/** Inclusive last N calendar days ending today (N=4 → today + previous 3). */
const daysAgoStr = (daysBack) => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - Math.max(0, daysBack));
  return toYmd(d);
};

const fmtDuration = (secs) => {
  const n = Number(secs);
  if (!Number.isFinite(n) || n <= 0) return '0s';
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = Math.floor(n % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ---- Recording URL extraction (same logic as WFH Telecaller Detail) ----
const SARV_RECORDING_BASE = 'https://s-ct3.sarv.com/v2/recording/direct';
const SARV_DEFAULT_USER_ID = '68798380';

const buildSarvRecordingUrl = (file, sarvUserId) => {
  if (!file || typeof file !== 'string') return null;
  const path = file.trim();
  if (!path) return null;
  const userId = String(sarvUserId || SARV_DEFAULT_USER_ID).trim() || SARV_DEFAULT_USER_ID;
  const sep = path.startsWith('/') ? '' : '/';
  return `${SARV_RECORDING_BASE}/${userId}${sep}${path}`;
};

const extractRecordingUrls = (call) => {
  const out = [];
  const seen = new Set();
  const push = (url, label) => {
    if (!url || typeof url !== 'string') return;
    const trimmed = url.trim();
    if (!trimmed || seen.has(trimmed)) return;
    if (!/^https?:\/\//i.test(trimmed)) return;
    seen.add(trimmed);
    out.push({ url: trimmed, label: label || `Recording ${out.length + 1}` });
  };

  const full = call?.full_row || {};
  const sarvUserId = full.sarv_user_id;

  if (full.sf_recording_url) push(full.sf_recording_url, full.sf_recording_name || 'Tata recording');

  const recs = call?.recordings ?? full.recordings;
  if (Array.isArray(recs)) {
    recs.forEach((r, i) => {
      if (typeof r === 'string') {
        if (/^https?:\/\//i.test(r)) push(r, `Recording ${i + 1}`);
        else push(buildSarvRecordingUrl(r, sarvUserId), `Sarv recording ${i + 1}`);
      } else if (r && typeof r === 'object') {
        const directUrl = r.url || r.recording || r.recording_url || r.location;
        if (directUrl && /^https?:\/\//i.test(String(directUrl))) {
          push(directUrl, r.name || r.label);
        } else if (r.file || r.path) {
          push(buildSarvRecordingUrl(r.file || r.path, sarvUserId), r.name || r.label || `Sarv recording ${i + 1}`);
        } else if (directUrl) {
          push(buildSarvRecordingUrl(directUrl, sarvUserId), r.name || r.label);
        }
      }
    });
  } else if (recs && typeof recs === 'object') {
    const directUrl = recs.url || recs.recording || recs.recording_url;
    if (directUrl && /^https?:\/\//i.test(String(directUrl))) push(directUrl, recs.name);
    else if (recs.file || recs.path) push(buildSarvRecordingUrl(recs.file || recs.path, sarvUserId), recs.name);
  }

  const raw = call?.recordings_raw ?? full.recordings_raw;
  if (typeof raw === 'string' && raw.trim()) {
    const trimmed = raw.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      push(trimmed, 'Recording');
    } else if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed);
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        arr.forEach((r, i) => {
          if (!r) return;
          if (typeof r === 'string') {
            if (/^https?:\/\//i.test(r)) push(r, `Recording ${i + 1}`);
            else push(buildSarvRecordingUrl(r, sarvUserId), `Sarv recording ${i + 1}`);
          } else if (typeof r === 'object') {
            const u = r.url || r.recording || r.recording_url || r.location;
            if (u && /^https?:\/\//i.test(String(u))) push(u, r.name);
            else if (r.file || r.path) {
              push(buildSarvRecordingUrl(r.file || r.path, sarvUserId), r.name || `Sarv recording ${i + 1}`);
            }
          }
        });
      } catch {
        /* ignore */
      }
    } else if (trimmed.startsWith('/')) {
      push(buildSarvRecordingUrl(trimmed, sarvUserId), 'Sarv recording');
    }
  }

  return out;
};

const detectProvider = (call) => {
  const full = call?.full_row || {};
  if (full.provider) return String(full.provider).toLowerCase();
  if (full.sf_uuid || full.sf_recording_url) return 'smartflo';
  if (full.sarv_user_id || full.sarv_camp_id) return 'sarv';
  return 'sarv';
};

export default function FaultDetector() {
  // Default: last 4 calendar days (today inclusive)
  const [fromDate, setFromDate] = useState(() => daysAgoStr(3));
  const [toDate, setToDate] = useState(() => todayStr());
  const [minMinutes, setMinMinutes] = useState('10');
  const [repeatTimes, setRepeatTimes] = useState('3');
  const [onlyWithRecording, setOnlyWithRecording] = useState(true);
  const [search, setSearch] = useState('');
  const [activeView, setActiveView] = useState('long'); // 'long' | 'repeat'
  const [expandedPhone, setExpandedPhone] = useState(null);

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [namesLoading, setNamesLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [fromCache, setFromCache] = useState(false);
  const [cacheAgeMs, setCacheAgeMs] = useState(0);
  const autoLoaded = useRef(false);
  const enrichGen = useRef(0);

  const enrichNamesInBackground = async (rows, rangeFrom, rangeTo) => {
    if (!rows?.length) return;
    const gen = ++enrichGen.current;
    setNamesLoading(true);
    try {
      // Prefer phones that matter for the dashboard cards (long ≥10m + repeats ≥3)
      const byPhone = new Map();
      for (const r of rows) {
        const p = String(r.customer_number10 || '').trim();
        if (!p) continue;
        if (!byPhone.has(p)) byPhone.set(p, []);
        byPhone.get(p).push(r);
      }
      const prefer = [];
      for (const [phone, calls] of byPhone.entries()) {
        const long = calls.some((c) => (c.effective_seconds || 0) >= 600);
        if (long || calls.length >= 3) prefer.push(phone);
      }
      const enriched = await FaultDetectorService.enrichCustomerNames(rows, prefer);
      if (gen !== enrichGen.current) return;
      setAllRows(enriched);
      FaultDetectorService.putCallsCache(rangeFrom, rangeTo, enriched);
    } catch (e) {
      console.warn('Name enrich failed:', e?.message || e);
    } finally {
      if (gen === enrichGen.current) setNamesLoading(false);
    }
  };

  const load = async (e, { force = false } = {}) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setNamesLoading(false);
    try {
      const withTimeout = Promise.race([
        FaultDetectorService.listCallsInRange({
          fromDate,
          toDate,
          force: !!force,
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Load timed out after 5 min — network/DB slow, Force refresh try karo')), 5 * 60 * 1000),
        ),
      ]);
      const result = await withTimeout;
      const rows = Array.isArray(result) ? result : result?.rows || [];
      setAllRows(rows);
      setFromCache(!!result?.fromCache);
      setCacheAgeMs(Number(result?.ageMs) || 0);
      setLoaded(true);
      setExpandedPhone(null);
      setLoading(false);
      // Names after UI is usable — never block the spinner
      if (rows.length && rows.some((r) => !r.customer_name)) {
        enrichNamesInBackground(rows, fromDate, toDate);
      }
    } catch (err) {
      setError(err.message || 'Could not load calls');
      setAllRows([]);
      setFromCache(false);
      setLoading(false);
    }
  };

  // Auto-load default last-4-days range once on mount
  useEffect(() => {
    if (autoLoaded.current) return;
    autoLoaded.current = true;
    void load(null, { force: false });
  }, []);

  const minSecs = useMemo(() => {
    const mins = Number(minMinutes);
    if (!Number.isFinite(mins) || mins < 0) return 0;
    return Math.round(mins * 60);
  }, [minMinutes]);

  const minRepeats = useMemo(() => {
    const n = Number(repeatTimes);
    if (!Number.isFinite(n) || n < 2) return 2;
    return Math.floor(n);
  }, [repeatTimes]);

  const longCalls = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = allRows.filter((r) => (r.effective_seconds || 0) >= minSecs);
    if (onlyWithRecording) {
      list = list.filter((r) => extractRecordingUrls(r).length > 0);
    }
    if (q) {
      list = list.filter((r) => {
        const hay = [
          r.telecaller_name,
          r.telecaller_phone,
          r.customer_number10,
          r.customer_name,
          r.call_disposition,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return [...list].sort((a, b) => (b.effective_seconds || 0) - (a.effective_seconds || 0));
  }, [allRows, minSecs, onlyWithRecording, search]);

  const repeatedGroups = useMemo(() => {
    const byPhone = new Map();
    for (const r of allRows) {
      const phone = String(r.customer_number10 || '').trim();
      if (!phone) continue;
      if (!byPhone.has(phone)) byPhone.set(phone, []);
      byPhone.get(phone).push(r);
    }

    const groups = [];
    for (const [phone, calls] of byPhone.entries()) {
      if (calls.length < minRepeats) continue;
      const sorted = [...calls].sort((a, b) => {
        const ta = new Date(a.last_hangup_time || a.first_answer_time || a.received_at || 0).getTime();
        const tb = new Date(b.last_hangup_time || b.first_answer_time || b.received_at || 0).getTime();
        return tb - ta;
      });
      const tcMap = new Map();
      let totalSecs = 0;
      let customerName = null;
      for (const c of sorted) {
        totalSecs += c.effective_seconds || 0;
        if (!customerName && c.customer_name) customerName = c.customer_name;
        if (c.telecaller_id) {
          tcMap.set(c.telecaller_id, c.telecaller_name || 'Unknown TC');
        }
      }
      groups.push({
        phone,
        customerName,
        count: sorted.length,
        calls: sorted,
        telecallers: Array.from(tcMap.values()),
        totalSecs,
      });
    }

    let list = groups.sort((a, b) => b.count - a.count || b.totalSecs - a.totalSecs);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((g) => {
        const hay = [g.phone, g.customerName, ...g.telecallers].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [allRows, minRepeats, search]);

  const longStats = useMemo(() => {
    const tcSet = new Set(longCalls.map((r) => r.telecaller_id).filter(Boolean));
    const totalSecs = longCalls.reduce((s, r) => s + (r.effective_seconds || 0), 0);
    return { calls: longCalls.length, tcs: tcSet.size, totalSecs };
  }, [longCalls]);

  const repeatStats = useMemo(() => {
    const numbers = repeatedGroups.length;
    const calls = repeatedGroups.reduce((s, g) => s + g.count, 0);
    const tcSet = new Set();
    repeatedGroups.forEach((g) => g.calls.forEach((c) => c.telecaller_id && tcSet.add(c.telecaller_id)));
    return { numbers, calls, tcs: tcSet.size };
  }, [repeatedGroups]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="text-amber-500" size={28} />
          Fault Detector
        </h1>
        <p className="text-gray-600 mt-1 max-w-3xl">
          Default last 4 days auto-load. Results 5 min cache — Force refresh se fresh DB pull.
          Cards pe Long / Repeated thresholds set karke details dekho.
        </p>
      </div>

      {/* Shared date filter */}
      <form
        onSubmit={load}
        className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 p-4 flex flex-wrap items-end gap-3"
      >
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">From date</span>
          <input
            type="date"
            required
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">To date</span>
          <input
            type="date"
            required
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Loading…' : 'Load calls'}
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={() => load(null, { force: true })}
          title="Ignore cache and fetch fresh from DB"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-sm font-medium text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 disabled:opacity-60"
        >
          Force refresh
        </button>
        {loaded && fromCache ? (
          <span className="text-xs text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 px-2.5 py-1 rounded-full">
            Cached · {Math.max(1, Math.round(cacheAgeMs / 1000))}s ago · 5 min TTL
          </span>
        ) : null}
        {namesLoading ? (
          <span className="text-xs text-indigo-700 bg-indigo-50 ring-1 ring-indigo-200 px-2.5 py-1 rounded-full">
            Customer names loading…
          </span>
        ) : null}
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by TC name, phone, customer number…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input
            type="checkbox"
            checked={onlyWithRecording}
            onChange={(e) => setOnlyWithRecording(e.target.checked)}
            className="rounded border-gray-300"
          />
          Long calls: only with recording
        </label>
      </form>

      {error ? (
        <div className="rounded-xl bg-red-50 text-red-800 px-4 py-3 text-sm ring-1 ring-red-200">{error}</div>
      ) : null}

      {/* Dashboard cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setActiveView('long')}
          className={`text-left rounded-2xl p-5 ring-1 transition shadow-sm ${
            activeView === 'long'
              ? 'bg-amber-50 ring-amber-300 shadow-amber-100'
              : 'bg-white ring-gray-200 hover:ring-amber-200'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                <Clock size={22} />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">Long Calls</div>
                <div className="text-xs text-gray-500 mt-0.5">Duration threshold card pe set karo</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-amber-700 tabular-nums">
                {loaded ? fmtNum(longStats.calls) : '—'}
              </div>
              <div className="text-xs text-gray-500">calls</div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="block" onClick={(e) => e.stopPropagation()}>
              <span className="block text-xs font-medium text-gray-600 mb-1">Above (minutes)</span>
              <input
                type="number"
                min="0"
                step="1"
                value={minMinutes}
                onChange={(e) => {
                  setMinMinutes(e.target.value);
                  setActiveView('long');
                }}
                className="w-28 rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
              />
            </label>
            <div className="text-xs text-gray-500 pb-2">
              {loaded ? (
                <>
                  {fmtNum(longStats.tcs)} TCs · {fmtDuration(longStats.totalSecs)} talk
                </>
              ) : (
                'Load date range pehle'
              )}
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setActiveView('repeat')}
          className={`text-left rounded-2xl p-5 ring-1 transition shadow-sm ${
            activeView === 'repeat'
              ? 'bg-violet-50 ring-violet-300 shadow-violet-100'
              : 'bg-white ring-gray-200 hover:ring-violet-200'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center">
                <Repeat2 size={22} />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">Repeated Calls</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Same customer number pe date range me kitni baar call gayi (har attempt count)
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-violet-700 tabular-nums">
                {loaded ? fmtNum(repeatStats.numbers) : '—'}
              </div>
              <div className="text-xs text-gray-500">numbers</div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-3">
            <label className="block" onClick={(e) => e.stopPropagation()}>
              <span className="block text-xs font-medium text-gray-600 mb-1">Repeat ≥ (times)</span>
              <input
                type="number"
                min="2"
                step="1"
                value={repeatTimes}
                onChange={(e) => {
                  setRepeatTimes(e.target.value);
                  setActiveView('repeat');
                }}
                className="w-28 rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-400/40"
              />
            </label>
            <div className="text-xs text-gray-500 pb-2">
              {loaded ? (
                <>
                  {fmtNum(repeatStats.calls)} calls · {fmtNum(repeatStats.tcs)} TCs
                </>
              ) : (
                'Load date range pehle'
              )}
            </div>
          </div>
        </button>
      </div>

      {/* Detail panel */}
      <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {activeView === 'long'
                ? `Long Calls ≥ ${minMinutes || 0} min`
                : `Numbers called ≥ ${minRepeats} times`}
            </h2>
            <p className="text-sm text-gray-500">
              {activeView === 'long'
                ? 'TC name + customer + recording ke saath duration list'
                : 'Repeat = chosen date range me same customer number par total call attempts (answered/short sab). Show calls pe recordings.'}
            </p>
          </div>
          {activeView === 'long' ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-2.5 py-1 rounded-full">
              <Phone size={12} />
              {fmtNum(longStats.calls)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 bg-violet-50 ring-1 ring-violet-200 px-2.5 py-1 rounded-full">
              <Repeat2 size={12} />
              {fmtNum(repeatStats.numbers)}
            </span>
          )}
        </div>

        {!loaded ? (
          <div className="px-4 py-16 text-center text-gray-500 text-sm">
            Date range choose karke <span className="font-semibold">Load calls</span> dabao.
          </div>
        ) : loading ? (
          <div className="px-4 py-16 text-center text-gray-500 text-sm">Loading calls…</div>
        ) : activeView === 'long' ? (
          longCalls.length === 0 ? (
            <div className="px-4 py-16 text-center text-gray-500 text-sm">
              Is range me {minMinutes || 0} min se lambi koi call nahi mili.
            </div>
          ) : (
            <LongCallsTable rows={longCalls} />
          )
        ) : repeatedGroups.length === 0 ? (
          <div className="px-4 py-16 text-center text-gray-500 text-sm">
            Koi number {minRepeats} ya usse zyada baar nahi mila.
          </div>
        ) : (
          <RepeatedCallsTable
            groups={repeatedGroups}
            expandedPhone={expandedPhone}
            setExpandedPhone={setExpandedPhone}
          />
        )}
      </div>
    </div>
  );
}

function LongCallsTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-600">
          <tr>
            <th className="px-4 py-3 font-medium whitespace-nowrap">Telecaller</th>
            <th className="px-4 py-3 font-medium whitespace-nowrap">Customer</th>
            <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Duration</th>
            <th className="px-4 py-3 font-medium whitespace-nowrap">Call time</th>
            <th className="px-4 py-3 font-medium whitespace-nowrap">Disposition</th>
            <th className="px-4 py-3 font-medium whitespace-nowrap">Provider</th>
            <th className="px-4 py-3 font-medium min-w-[280px]">Recording</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => {
            const recordings = extractRecordingUrls(row);
            const provider = detectProvider(row);
            const callTime = row.last_hangup_time || row.first_answer_time || row.received_at;
            return (
              <tr key={row.id} className="hover:bg-gray-50/80 align-top">
                <td className="px-4 py-3">
                  <div className="font-semibold text-gray-900">{row.telecaller_name || 'Unknown TC'}</div>
                  {row.telecaller_phone ? (
                    <div className="text-xs text-gray-500 mt-0.5">{row.telecaller_phone}</div>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-gray-900">{row.customer_name || '—'}</div>
                  <div className="font-mono text-xs text-gray-500 mt-0.5">{row.customer_number10 || '—'}</div>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 ring-1 ring-amber-200 tabular-nums whitespace-nowrap">
                    {fmtDuration(row.effective_seconds)}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{fmtDateTime(callTime)}</td>
                <td className="px-4 py-3 text-gray-700">{row.call_disposition || row.call_status || '—'}</td>
                <td className="px-4 py-3">
                  <ProviderBadge provider={provider} />
                </td>
                <td className="px-4 py-3">
                  <RecordingList callId={row.id} recordings={recordings} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RepeatedCallsTable({ groups, expandedPhone, setExpandedPhone }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-600">
          <tr>
            <th className="px-4 py-3 font-medium whitespace-nowrap">Customer</th>
            <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Times</th>
            <th className="px-4 py-3 font-medium">Telecallers</th>
            <th className="px-4 py-3 font-medium whitespace-nowrap text-right">Total talk</th>
            <th className="px-4 py-3 font-medium">Detail</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {groups.map((g) => {
            const open = expandedPhone === g.phone;
            return (
              <React.Fragment key={g.phone}>
                <tr className="hover:bg-gray-50/80">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">{g.customerName || 'Unknown customer'}</div>
                    <div className="font-mono text-xs text-gray-500 mt-0.5">{g.phone}</div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-50 text-violet-800 ring-1 ring-violet-200 tabular-nums">
                      {fmtNum(g.count)}×
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {g.telecallers.length ? g.telecallers.join(', ') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-800">{fmtDuration(g.totalSecs)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setExpandedPhone(open ? null : g.phone)}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      {open ? 'Hide calls' : 'Show calls'}
                    </button>
                  </td>
                </tr>
                {open ? (
                  <tr className="bg-violet-50/40">
                    <td colSpan={5} className="px-4 py-3">
                      <div className="space-y-2">
                        {g.calls.map((row) => {
                          const recordings = extractRecordingUrls(row);
                          const provider = detectProvider(row);
                          const callTime =
                            row.last_hangup_time || row.first_answer_time || row.received_at;
                          return (
                            <div
                              key={row.id}
                              className="bg-white rounded-lg ring-1 ring-gray-200 px-3 py-2 flex flex-wrap items-center gap-3"
                            >
                              <div className="min-w-[140px]">
                                <div className="text-sm font-semibold text-gray-900">
                                  {row.telecaller_name || 'Unknown TC'}
                                </div>
                                <div className="text-xs text-gray-500">{fmtDateTime(callTime)}</div>
                              </div>
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 ring-1 ring-amber-200 tabular-nums">
                                {fmtDuration(row.effective_seconds)}
                              </span>
                              <span className="text-xs text-gray-600">
                                {row.call_disposition || row.call_status || '—'}
                              </span>
                              <ProviderBadge provider={provider} />
                              <div className="flex-1 min-w-[200px]">
                                <RecordingList callId={row.id} recordings={recordings} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProviderBadge({ provider }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${
        provider === 'smartflo'
          ? 'bg-orange-50 text-orange-700 ring-orange-200'
          : 'bg-sky-50 text-sky-700 ring-sky-200'
      }`}
    >
      {provider === 'smartflo' ? 'Tata' : 'Sarv'}
    </span>
  );
}

function RecordingList({ callId, recordings }) {
  if (!recordings.length) {
    return <span className="text-xs text-gray-400 italic">No recording</span>;
  }
  return (
    <div className="space-y-2">
      {recordings.map((rec, idx) => (
        <div key={`${callId}-${idx}`} className="flex items-center gap-2">
          <audio controls preload="none" src={rec.url} className="h-8 max-w-[240px]" />
          <a
            href={rec.url}
            target="_blank"
            rel="noreferrer"
            title="Open in new tab"
            className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
          >
            <ExternalLink size={14} />
          </a>
        </div>
      ))}
    </div>
  );
}
